import { guardNumber } from "../numbers";
import type { CalculationFormPositionItem, CalculationFormServiceItem } from "../schemas";
import type { NullableFields, ServiceUnit } from "../types";
import { calculateCubicMeters } from "./calculate-cubic-meters";
import { calculateSquareMeters } from "./calculate-square-meters";
import { serviceUnits } from "./service-units";

type Measurable = {
	quantity?: number | null;
	meters?: number | null;
	length?: number | null;
	width?: number | null;
	height?: number | null;
	manualMeasurement?: number | null;
};

/**
 * Determines the quantity multiplier applied on top of a position's measurement (e.g. "3
 * segments of 5m each"). Positions created before this multiplier existed have no stored
 * `quantity`, so an unset value falls back to `1` rather than `0` to avoid zeroing out their
 * total; an explicit `0` is still honored.
 *
 * @param item - The position to read the quantity from.
 * @returns The multiplier to apply to the position's measurement.
 */
function calculateQuantityMultiplier(item: Measurable): number {
	return item.quantity == null ? 1 : guardNumber(item.quantity);
}

/**
 * Calculates the raw measurement (piece count, running meters, area or volume) for a
 * unit type from a set of dimension fields, without applying a price. For every unit
 * except piece, the position's `quantity` acts as a multiplier over the measured
 * dimensions (e.g. quantity 3 × 5 meters = 15 meters); for piece, `quantity` already
 * is the measurement, so it is not multiplied again. When `manualMeasurement` is set,
 * it replaces the dimension-based calculation (for when the user knows the result but
 * not the exact dimensions) while `quantity` still multiplies on top of it.
 *
 * @param unit - The unit type determining which dimension fields to read.
 * @param item - The dimension fields to measure.
 * @returns The measurement in the unit's own quantity (pieces, m, m² or m³).
 */
export function calculateMeasurementForUnit(unit: ServiceUnit, item: Measurable): number {
	if (unit === "piece" || unit === "piece-week") return guardNumber(item.quantity);
	if (item.manualMeasurement != null)
		return guardNumber(item.manualMeasurement) * calculateQuantityMultiplier(item);
	if (unit === "meters" || unit === "meters-week")
		return guardNumber(item.meters) * calculateQuantityMultiplier(item);
	if (unit === "cubic-meters")
		return calculateCubicMeters(item) * calculateQuantityMultiplier(item);
	if (unit === "square-meters" || unit === "square-meters-week")
		return calculateSquareMeters(item) * calculateQuantityMultiplier(item);
	return 0;
}

/**
 * Calculates a single position's monetary contribution to its service total: its measurement
 * (for the service unit) multiplied by the service's unit price.
 *
 * @param unit - The service's unit type, used to measure the position.
 * @param position - The position to price.
 * @param unitPrice - The service's single unit price.
 * @returns The position's contribution in currency.
 */
export function calculatePositionTotal(
	unit: ServiceUnit,
	position: NullableFields<CalculationFormPositionItem>,
	unitPrice?: number | null,
): number {
	if (!serviceUnits.includes(unit)) return 0;
	return calculateMeasurementForUnit(unit, position) * guardNumber(unitPrice);
}

/**
 * This function calculates the total price for a service item based on its unit type and its
 * named positions (e.g. multiple measured wall segments under one service): each position
 * contributes its measurement × the service's single unit price.
 * @param item
 * @returns
 */
export function calculateService(item: NullableFields<CalculationFormServiceItem>) {
	const unit = item.unit as ServiceUnit;
	const positions = item.positions ?? [];
	return positions.reduce(
		(sum, position) => sum + calculatePositionTotal(unit, position, item.unitPrice),
		0,
	);
}
